import storage from "redux-persist/lib/storage";

import { subscribeToAuthChanges } from "../utils/authUtils";
import { clearStaffPersonaSession } from "../utils/staffPersonaSession";
import { PERSISTED_STORAGE_KEYS, RETIRED_STORAGE_KEYS } from "./rootReducer";

/**
 * Which identity the persisted state in this browser belongs to.
 *
 * Deliberately outside the `persist:` namespace, so redux-persist neither
 * rehydrates it nor clears it with everything else, and so this module is its
 * only writer. That single-writer property is what makes the comparison below
 * reliable: `auth.user.uid` is assigned by several reducers at times nobody
 * controls, and a marker only this module touches cannot be moved out from
 * under the comparison.
 */
export const STORAGE_OWNER_KEY = "dashone:storage-owner";

/**
 * How long to wait for Firebase before hydrating anyway.
 *
 * `PersistGate` opens on `bootstrapped`, which only flips once a registered
 * slice rehydrates, so a `persist()` that never runs renders nothing at all.
 * An unreachable Firebase must cost a stale first paint, never a blank app.
 */
export const AUTH_WAIT_MS = 4000;

const readOwner = () => {
  try {
    return localStorage.getItem(STORAGE_OWNER_KEY);
  } catch {
    // A browser blocking storage has no owner to read and no state to protect.
    return null;
  }
};

const writeOwner = (uid) => {
  try {
    localStorage.setItem(STORAGE_OWNER_KEY, uid);
  } catch {
    // Nothing to do: the next load reads null and clears again, which is the
    // safe direction to fail in.
  }
};

const clearOwner = () => {
  try {
    localStorage.removeItem(STORAGE_OWNER_KEY);
  } catch {
    // Nothing to do; the next load re-reads whatever is there and re-decides.
  }
};

// True when the persisted auth blob says a "View as User" session is in
// progress. Read from storage rather than the store, because at boot the store
// has not rehydrated yet and this decides whether it may.
//
// redux-persist serialises each field of a slice separately, so the flag is a
// JSON string inside a JSON object.
const impersonationInProgress = () => {
  try {
    const blob = JSON.parse(localStorage.getItem("persist:auth") || "{}");
    return JSON.parse(blob.isImpersonating || "false") === true;
  } catch {
    return false;
  }
};

// Drop every persisted blob, plus the keys no build writes any more.
// `PERSISTED_STORAGE_KEYS` derives from `SLICE_PERSIST_CONFIGS`, so it cannot
// miss a slice the store actually persists. `RETIRED_STORAGE_KEYS` is
// hand-maintained, and covers only what a previous build wrote.
//
// `redux-persist/lib/storage` wraps a synchronous `localStorage.removeItem` in a
// resolved Promise, so the key is gone the instant this returns even though the
// call looks async — which is what makes it safe to clear and then navigate away
// in the same tick.
const clearPersistedState = () => {
  [...PERSISTED_STORAGE_KEYS, ...RETIRED_STORAGE_KEYS].forEach((key) => {
    storage.removeItem(key).catch(() => {});
  });
  // A staff member's onboarding answers live in sessionStorage, outside the
  // lists above. They are stamped with the uid that wrote them, so a stranger
  // cannot read them, and a boot that rejects the stored owner is the moment
  // to drop them anyway.
  clearStaffPersonaSession();
};

// Re-enter the boot path from a clean slate. After a mid-session handoff clears
// storage, a reload runs the boot decision below against the empty store and
// hydrates to initial state. An in-place reset cannot stand in for the reload:
// `appReducer(undefined, action)` strips each slice's `_persist`, and
// `persistReducer` then passes every later action straight through without
// writing, so a reset reaches only memory and leaves the prior blobs on disk.
const reloadFromCleanSlate = () => {
  window.location.assign("/");
};

/**
 * Hold hydration until Firebase says who is here, then keep watching the store.
 *
 * redux-persist begins reading storage the moment it is told to, so the only
 * way to keep one person's state out of another's session is to not read it
 * until the identity is known. The boot half below gates that first read.
 *
 * After boot, the *store* — not raw Firebase — is the source of the current
 * identity, which is what makes the mid-session check race-free. A store
 * subscriber sees `auth.user` and `auth.isImpersonating` in one atomic snapshot,
 * because a single reducer sets both, so an impersonation start cannot be
 * misread as a stranger signing in. `onAuthStateChanged` fires on the Firebase
 * token swap before that reducer runs, so watching it directly would see the
 * new uid without the flag.
 *
 * Runs from `main.jsx` rather than a component, so React StrictMode's
 * double-invoked effects cannot subscribe twice.
 *
 * @param {{persist: () => void}} persistor - the store's persistor.
 * @param {{dispatch: Function, getState: Function, subscribe: Function}} store
 */
export const startPersistenceWhenIdentityKnown = (persistor, store) => {
  let hydrated = false;
  let watching = false;
  // Whether a real Firebase answer gated the boot. False means the bounded wait
  // expired first and the store was read without knowing the owner.
  let bootVerified = false;

  const hydrate = () => {
    if (hydrated) return;
    hydrated = true;
    persistor.persist();
  };

  // Decide what a signed-in uid (or a signed-out `null`) means for state that is
  // already loaded, and act. Never dispatches, so it cannot loop.
  const reconcile = (uid) => {
    const owner = readOwner();

    if (!uid) {
      // Signed out, so there is no owner: drop the marker to match the
      // signed-out boot path, so a later sign-in establishes cleanly rather
      // than reading a stale owner and taking an extra clear-and-reload.
      clearOwner();
      // With a verified boot the logout flow already purged the persisted keys.
      // After a blind boot the loaded state was never checked against an owner,
      // so clear it and reload.
      if (!bootVerified && owner) {
        clearPersistedState();
        reloadFromCleanSlate();
      }
      return;
    }

    if (uid === owner) return;

    // A cleared boot left no owner, so this sign-in establishes one rather than
    // replacing anyone. The store is already empty; just record the owner.
    if (!owner) {
      writeOwner(uid);
      return;
    }

    // A different person over state that is already loaded. Record the new
    // owner, clear, and reload into the boot path.
    writeOwner(uid);
    clearPersistedState();
    reloadFromCleanSlate();
  };

  const watchStore = () => {
    if (watching) return;
    watching = true;
    store.subscribe(() => {
      const auth = store.getState().auth;
      // Impersonation swaps identity on purpose; its own reset runs through the
      // rootReducer's impersonation branch. The flag is read in the same atomic
      // snapshot as the user, so the swap can never be seen without it.
      if (auth?.isImpersonating) return;
      reconcile(auth?.user?.uid ?? null);
    });
  };

  // Hydrate anyway if Firebase never answers, and start watching so a late
  // answer still reconciles the state that was loaded blind.
  const fallback = setTimeout(() => {
    hydrate();
    watchStore();
  }, AUTH_WAIT_MS);

  subscribeToAuthChanges((firebaseUser) => {
    const arriving = firebaseUser?.uid ?? null;

    // The boot decision is made on the first answer; the store watcher owns
    // everything after it.
    if (hydrated) return;

    clearTimeout(fallback);
    bootVerified = true;

    // Impersonation deliberately runs under the target's Firebase identity while
    // the persisted state stays the admin's, so at boot the uid always disagrees
    // with the marker. Clearing on that would drop the flags `authPersistConfig`
    // persists precisely so a reload keeps the Exit control, stranding the admin
    // inside the target's account.
    if (impersonationInProgress()) {
      hydrate();
      watchStore();
      return;
    }

    // No session means no state to restore. OWASP ASVS requires authenticated
    // data to be cleared from client storage when a session ends, and a user who
    // closes the tab ends theirs without the logout purge ever running. This is
    // the next moment the app can observe that, so it clears here. It also keeps
    // the sign-in that follows simple: an empty store means that sign-in records
    // an owner rather than undoing one.
    if (!arriving) {
      clearPersistedState();
      clearOwner();
      hydrate();
      watchStore();
      return;
    }

    // A signed-in boot. Clear before reading if the marker names someone else,
    // where an absent marker counts as someone else so an upgrade shuts a leak a
    // browser is already carrying.
    if (arriving !== readOwner()) clearPersistedState();
    writeOwner(arriving);
    hydrate();
    watchStore();
  });
};
